import { useEffect, useMemo, useState } from 'react';
import {
  BarreOutils, Carte, Message, Selecteur, SqueletteTableau, Vide, useColonnes,
} from '@journal/ui';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { useSession } from '../lib/session.tsx';
import {
  TableauControles, niveau, type Domaine, type Eleve, type Moyenne,
} from '../components/TableauControles.tsx';

/**
 * Évaluation par contrôles — matière, puis ses domaines.
 *
 * ── Deux niveaux de navigation, pas trois ───────────────────────────────────
 * On choisit une matière ; tous ses domaines s'affichent, chacun avec son
 * tableau. La v1 a essayé d'intercaler les subdivisions du référentiel
 * (« CP — Lire à voix haute ») avant d'y renoncer : ce n'est pas ainsi qu'un
 * enseignant range une dictée.
 */

interface Chargement {
  students: Eleve[];
  domains: Domaine[];
  marks: Array<{ assessmentId: string; studentId: string; level: string }>;
  averages: Moyenne[];
}

export function ControlesEvaluation({ matieres, version = 0, surMatiere }: {
  matieres: string[];
  /** La matière affichée : l'impression de l'écran parent imprime celle-là. */
  surMatiere?: (matiere: string) => void;
  /** Change quand la liste de la classe change : le tableau se recharge. */
  version?: number;
}) {
  const { api } = useSession();
  const [matiere, setMatiere] = useState('');
  const matiereActive = matiere || matieres[0] || '';
  useEffect(() => { surMatiere?.(matiereActive); }, [matiereActive, surMatiere]);

  const donnees = useDonnees<Chargement>(
    matiereActive ? `/assessments?subject=${encodeURIComponent(matiereActive)}` : null,
    [matiereActive, version],
  );
  const { executer, erreur } = useEcriture();

  /*
   * Index des notes, construit une fois.
   *
   * Le tableau les cherche par couple contrôle × élève. Sur une classe de
   * vingt-cinq élèves et quatre contrôles, un `find` par case ferait cent
   * parcours de la liste à chaque rendu — pour une donnée qui ne bouge pas.
   */
  const notes = useMemo(() => {
    const index = new Map<string, string>();
    for (const m of donnees.donnees?.marks ?? []) {
      index.set(`${m.assessmentId}:${m.studentId}`, m.level);
    }
    return index;
  }, [donnees.donnees]);

  if (matieres.length === 0) {
    return (
      <Vide titre="Aucune matière dans le programme">
        Le référentiel ne contient aucune matière pour les niveaux enseignés.
      </Vide>
    );
  }

  const recharger = () => donnees.recharger();

  return (
    <div className="flex flex-col gap-4">
      {erreur && <Message ton="erreur">{erreur}</Message>}
      {donnees.erreur && <Message ton="erreur">{donnees.erreur}</Message>}

      <BarreOutils>
        <Selecteur
          libelle="Matière"
          value={matiereActive}
          onChange={(e) => setMatiere(e.target.value)}
          options={matieres.map((m) => ({ valeur: m, texte: m }))}
        />
      </BarreOutils>

      {donnees.chargement && !donnees.donnees && (
        <Carte><SqueletteTableau lignes={6} colonnes={4} /></Carte>
      )}

      {donnees.donnees?.students.length === 0 && (
        <Vide titre="Aucun élève dans cette classe">
          Renseignez votre liste de classe pour commencer à évaluer.
        </Vide>
      )}

      {(donnees.donnees?.students.length ?? 0) > 0
        && donnees.donnees!.domains.map((domaine) => (
          <TableauControles
            key={domaine.name}
            matiere={matiere}
            domaine={domaine}
            eleves={donnees.donnees!.students}
            notes={notes}
            moyennes={donnees.donnees!.averages}
            surNote={(controleId, eleveId, valeur) => {
              void executer(async () => {
                await api.requete(`/assessments/${controleId}/marks`, {
                  methode: 'PUT', corps: { studentId: eleveId, level: valeur },
                });
                await recharger();
              });
            }}
            surAjout={async (libelle) => {
              await executer(async () => {
                await api.requete('/assessments', {
                  methode: 'POST',
                  corps: { subject: matiereActive, domainName: domaine.name, label: libelle },
                });
                await recharger();
              });
            }}
            surRenommage={async (controleId, libelle) => {
              await executer(async () => {
                await api.requete(`/assessments/${controleId}`, {
                  methode: 'PATCH', corps: { label: libelle },
                });
                await recharger();
              });
            }}
            surSuppression={async (controleId) => {
              await executer(async () => {
                await api.requete(`/assessments/${controleId}`, { methode: 'DELETE' });
                await recharger();
              });
            }}
          />
        ))}
    </div>
  );
}

// ── Statistiques ─────────────────────────────────────────────────────────────

interface LigneStats {
  studentId: string;
  lastName: string;
  firstName: string;
  subjects: Array<{
    name: string;
    domains: Array<{ name: string; level: string | null; count: number }>;
  }>;
}

/**
 * Un tableau par matière, un élève par ligne, une colonne par domaine évalué.
 *
 * ── Aucune moyenne de matière ───────────────────────────────────────────────
 * La v1 en affichait une, puis l'a retirée. Additionner « Lecture » et
 * « Grammaire » produit un chiffre qui ne désigne rien : on ne peut ni le
 * commenter en conseil de cycle, ni en tirer une action.
 *
 * ── Des niveaux, jamais des nombres ─────────────────────────────────────────
 * Y compris ici. Une colonne de moyennes chiffrées se classe, et une classe
 * d'élèves classés est exactement ce que l'évaluation par compétences existe
 * pour éviter.
 */
export function StatistiquesControles({ version = 0 }: { version?: number } = {}) {
  const stats = useDonnees<LigneStats[]>('/assessments/stats', [version]);

  if (stats.chargement && !stats.donnees) {
    return <Carte><SqueletteTableau lignes={6} colonnes={4} /></Carte>;
  }
  if (stats.erreur) return <Message ton="erreur">{stats.erreur}</Message>;

  const lignes = stats.donnees ?? [];

  // Les matières RÉELLEMENT évaluées, dans l'ordre. Afficher un tableau vide
  // par matière du programme ferait défiler des écrans de tirets.
  const matieres = [...new Set(lignes.flatMap((l) => l.subjects.map((s) => s.name)))]
    .sort((a, b) => a.localeCompare(b, 'fr'));

  if (matieres.length === 0) {
    return (
      <Vide titre="Aucun contrôle noté">
        Les statistiques se remplissent au fur et à mesure des notations.
      </Vide>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {matieres.map((matiere) => (
        <TableauStatistiques key={matiere} matiere={matiere} lignes={lignes} />
      ))}
    </div>
  );
}

/**
 * Le tableau d'UNE matière.
 *
 * Composant à part, et pas une boucle dans le précédent : `useColonnes` est un
 * crochet, et un crochet appelé dans une boucle change de position dès qu'une
 * matière est évaluée pour la première fois — ce que React interdit. Le
 * découpage n'est donc pas cosmétique.
 */
function TableauStatistiques({
  matiere, lignes,
}: {
  matiere: string;
  lignes: LigneStats[];
}) {
  const colonnes = useColonnes(`stats-controles.${matiere}`);

  const domaines = [...new Set(
    lignes.flatMap((l) => l.subjects
      .filter((s) => s.name === matiere)
      .flatMap((s) => s.domains.map((d) => d.name))),
  )].sort((a, b) => a.localeCompare(b, 'fr'));

  return (
    <Carte titre={matiere} sansRembourrage>
      <div data-impression="evaluation" className="impression-paysage overflow-x-auto">
        <table className="w-full border-collapse text-[13px]">
          {colonnes.colgroup(['Élève', ...domaines])}
          <thead>
            <tr>
              <th
                className="relative sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 text-left font-medium"
                style={colonnes.entete('Élève').style}
              >
                Élève
                {colonnes.poignee('Élève')}
              </th>
              {domaines.map((d) => (
                <th
                  key={d}
                  className="relative border-b border-l border-[var(--color-border)] px-2 py-2 text-center font-medium"
                  style={colonnes.entete(d).style}
                >
                  {d}
                  {colonnes.poignee(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const parDomaine = new Map(
                l.subjects.filter((s) => s.name === matiere)
                  .flatMap((s) => s.domains.map((d) => [d.name, d] as const)),
              );
              return (
                <tr key={l.studentId}>
                  <td className="sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 whitespace-nowrap">
                    {l.lastName} {l.firstName}
                  </td>
                  {domaines.map((d) => {
                    const cellule = parDomaine.get(d);
                    const info = niveau(cellule?.level ?? null);
                    return (
                      <td
                        key={d}
                        className="border-b border-l border-[var(--color-border)] px-2 py-1.5 text-center text-[12px] font-semibold"
                        style={{
                          backgroundColor: cellule?.level ? `${info.couleur}1f` : 'transparent',
                          color: cellule?.level ? 'var(--color-fg)' : 'var(--color-fg-muted)',
                        }}
                        title={cellule ? `${cellule.count} contrôle(s) noté(s)` : 'Non évalué'}
                      >
                        {/*
                          * Le LIBELLÉ, jamais une note : une colonne de
                          * moyennes chiffrées se classe, et classer des élèves
                          * est ce que l'évaluation par compétences évite.
                          *
                          * Le NOMBRE de contrôles, en revanche, qualifie le
                          * niveau et doit se voir : « atteint » sur un contrôle
                          * ne vaut pas « atteint » sur douze. En infobulle
                          * seulement, il n'existait que pour la souris.
                          */}
                        {cellule?.level
                          ? `${info.titre} (${cellule.count})`
                          : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Carte>
  );
}
